import React from "react";
import moment from "moment";
import axiosInstance from "../../Services/axiosCommon";

/**
 * Default fallback checklist configuration for PO verification.
 */
export const DEFAULT_PO_CHECKLIST = [
  { id: "supplierPendingDeliveries", label: "Supplier pending deliveries verified." },
  { id: "customerDues", label: "Customer dues verified" },
  { id: "customerPaymentTerms", label: "Customer payment terms verified" },
];

/**
 * Normalizes backend checklist items into uniform { id, label } structure.
 */
export const normalizeChecklistItems = (rawItems = []) => {
  if (!Array.isArray(rawItems) || rawItems.length === 0) return [];
  return rawItems
    .filter((item) => item && (item.isActive !== false && item.is_active !== false))
    .map((item, idx) => ({
      id: String(item.id || item._id || item.value || `option_${idx + 1}`),
      label: item.value || item.label || item.name || `Item ${idx + 1}`,
      raw: item,
    }));
};

/**
 * Fetches active checklist items from backend /po-checklist API.
 */
export const fetchChecklistByStatus = async (status = "verify") => {
  try {
    const res = await axiosInstance.get(`/po-checklist/?status=${status}`);
    const list = res?.data?.data || res?.data || [];
    const normalized = normalizeChecklistItems(list);
    return normalized;
  } catch (err) {
    console.warn(`Could not fetch dynamic checklist for ${status}:`, err);
    return [];
  }
};

/**
 * Parses individual checklist option values coming from the backend.
 * Handles arrays: ["true"], ["true", dateTime, userId] or primitive boolean/string.
 */
export const parseChecklistOption = (opt) => {
  if (Array.isArray(opt)) {
    return {
      status:
        opt[0] === true || String(opt[0]).toLowerCase() === "true"
          ? "true"
          : "false",
      dateTime: opt[1] || "",
      userId: opt[2] !== undefined ? opt[2] : null,
    };
  }
  if (typeof opt === "boolean" || typeof opt === "string") {
    return {
      status:
        opt === true || String(opt).toLowerCase() === "true"
          ? "true"
          : "false",
      dateTime: "",
      userId: null,
    };
  }
  return { status: "false", dateTime: "", userId: null };
};

/**
 * Parses full checkboxes map from API response for given items.
 * Matches by item.id, item.label, item.value, or index-based fallback.
 */
export const parseChecklistMap = (checkboxs = {}, items = []) => {
  if (!items || items.length === 0) {
    return { parsedData: {}, submittedMap: {} };
  }
  const parsedData = {};
  const submittedMap = {};

  const findRawVal = (item, idx) => {
    if (!checkboxs || typeof checkboxs !== "object") return undefined;
    if (checkboxs[item.id] !== undefined) return checkboxs[item.id];
    if (item.label && checkboxs[item.label] !== undefined) return checkboxs[item.label];
    if (item.raw?.value && checkboxs[item.raw.value] !== undefined) return checkboxs[item.raw.value];
    if (checkboxs[`option${idx + 1}`] !== undefined) return checkboxs[`option${idx + 1}`];

    const targetLabel = (item.label || item.raw?.value || "").trim().toLowerCase();
    if (targetLabel) {
      const matchKey = Object.keys(checkboxs).find(
        (k) => k.trim().toLowerCase() === targetLabel
      );
      if (matchKey && checkboxs[matchKey] !== undefined) {
        return checkboxs[matchKey];
      }
    }
    return undefined;
  };

  items.forEach((item, idx) => {
    const rawVal = findRawVal(item, idx);
    const parsed = parseChecklistOption(rawVal);
    parsedData[item.id] = parsed;
    submittedMap[item.id] = parsed.status === "true";
  });

  return { parsedData, submittedMap };
};

/**
 * Builds standard verification or review payload for API submission.
 */
export const buildChecklistPayload = (
  currentData,
  items = [],
  currentUserId = null
) => {
  if (!items || items.length === 0) {
    return { checkboxs: {}, global_true: "true" };
  }

  const isAllChecked = items.every(
    (item) => currentData?.[item.id]?.status === "true"
  );

  const checkboxs = {};
  items.forEach((item) => {
    const itemData = currentData?.[item.id];
    const key = item.label || item.id;
    const isChecked = itemData?.status === "true";

    checkboxs[key] = [
      isChecked ? "true" : "false",
      itemData?.dateTime || moment().format("DD-MM-YYYY HH:mm:ss"),
      itemData?.userId ?? currentUserId,
    ];
  });

  return {
    checkboxs,
    global_true: isAllChecked ? "true" : "false",
  };
};

/**
 * Reusable Checklist Form for verification / review modals.
 */
export const VerificationChecklistForm = ({
  items = [],
  verificationData = {},
  submittedOptions = {},
  onChange,
  disabled = false,
  description = "Please complete the checklist to proceed:",
}) => {
  if (!items || items.length === 0) return null;
  return (
    <div className="py-2 min-w-[280px] sm:min-w-[340px] max-w-md">
      {description && (
        <p className="text-xs text-[#606279] mb-3">{description}</p>
      )}
      <div className="flex flex-col gap-2 bg-[#f8fafc] p-3 rounded-lg border border-gray-200">
        {items.map((item) => {
          const isChecked = verificationData?.[item.id]?.status === "true";
          const itemDate = verificationData?.[item.id]?.dateTime;
          const isSubmitted = !!submittedOptions?.[item.id] || disabled;

          return (
            <label
              key={item.id}
              className={`flex items-start gap-3 p-2.5 rounded-md transition-all border ${
                isSubmitted
                  ? "bg-emerald-50/60 border-emerald-300 text-[#111537] cursor-not-allowed opacity-90"
                  : isChecked
                  ? "bg-emerald-50/80 border-emerald-300 text-[#111537] cursor-pointer"
                  : "bg-white border-gray-200 hover:border-gray-300 text-[#334155] cursor-pointer"
              }`}
            >
              <input
                type="checkbox"
                checked={isChecked}
                disabled={isSubmitted}
                onChange={(e) => onChange?.(item.id, e.target.checked)}
                className={`mt-0.5 w-4 h-4 rounded border-gray-300 text-baseColor-primary accent-baseColor-primary shrink-0 ${
                  isSubmitted ? "cursor-not-allowed" : "cursor-pointer"
                }`}
              />
              <div className="flex flex-col flex-1">
                <span className="text-xs font-medium select-none">
                  {item.label}
                </span>
                {itemDate && isChecked && (
                  <span className="text-[10px] text-gray-400 mt-0.5 select-none">
                    Verified: {itemDate}
                  </span>
                )}
              </div>
            </label>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Reusable Checklist History & Progress display for status view modal.
 */
export const VerificationChecklistHistory = ({
  items = [],
  checkboxs = {},
  title = "Checklist:",
}) => {
  if (!checkboxs || typeof checkboxs !== "object") return null;

  const entries = Object.entries(checkboxs);
  if (entries.length === 0) return null;

  const validEntries = entries.filter(([key, val]) => {
    const parsed = parseChecklistOption(val);
    return parsed.status === "true";
  });

  if (validEntries.length === 0) return null;

  return (
    <div className="mt-2 bg-[#f8fafc] p-2.5 rounded-md border border-gray-200">
      <p className="text-[11px] font-semibold text-[#111537] mb-1.5">{title}</p>
      <div className="flex flex-col gap-1.5">
        {validEntries.map(([key, checkData], idx) => {
          const dateTime = Array.isArray(checkData) ? checkData[1] : "";
          const verifiedBy = Array.isArray(checkData) ? checkData[2] : "";

          // Resolve display label
          const matchedItem = items?.find?.(
            (i) => i.id === key || i.label === key || i.raw?.value === key
          );
          const displayLabel = matchedItem?.label || key;

          return (
            <div
              key={key || idx}
              className="flex items-start gap-1.5 text-[11px] text-[#334155]"
            >
              <span className="font-bold text-emerald-600">✓</span>
              <div className="flex-1">
                <span className="font-medium">{displayLabel}</span>
                {dateTime && (
                  <div className="text-[10px] text-[#606279] mt-0.5">
                    {dateTime}
                    {verifiedBy ? ` by ${verifiedBy}` : ""}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default VerificationChecklistForm;
